import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import SetupTrackPicker, { trackFromSearch, type SetupTrack } from '@/components/SetupTrackPicker';
import { useTranslation } from 'react-i18next';
const logo = "https://storage.googleapis.com/connct-cba18.firebasestorage.app/frontend-assets/Conncct-logo.png";
import { API_BASE_URL } from '../utils/env';
const dm2 = "https://storage.googleapis.com/connct-cba18.firebasestorage.app/frontend-assets/dem2.png";

export default function Dm2() {
    const navigate = useNavigate();
    const location = useLocation();
    const { t, i18n } = useTranslation();
    const [formData, setFormData] = useState({
        setupTrack: trackFromSearch(location.search) as SetupTrack | '',
        helpWith: '',
        companyName: '',
        discussionTopic: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    const handleInputChange = (field: string, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const toggleLanguage = () => {
        const newLang = i18n.language === 'en' ? 'ar' : 'en';
        i18n.changeLanguage(newLang);
    };

    const handleBack = () => {
        navigate('/demo1');
    };

    const handleDone = async () => {
        if (!formData.setupTrack || !formData.helpWith || !formData.companyName || !formData.discussionTopic) {
            setError(t('demo.fillAllFields'));
            return;
        }

        // Combine with data from demo1
        const demo1Data = location.state || {};
        if (!demo1Data.firstName || !demo1Data.lastName || !demo1Data.email) {
            setError(t('demo.missingInfo'));
            navigate('/demo1');
            return;
        }

        const completeData = { ...demo1Data, ...formData };

        setIsSubmitting(true);
        setError('');

        try {
            const response = await fetch(`${API_BASE_URL}/demo/submit`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(completeData),
            });

            // Check if response has content before parsing JSON
            const contentType = response.headers.get('content-type');
            let result = null;

            if (contentType && contentType.includes('application/json')) {
                const text = await response.text();
                if (text) {
                    try {
                        result = JSON.parse(text);
                    } catch (parseError) {
                        console.error('JSON parse error:', parseError);
                        throw new Error('Invalid response from server');
                    }
                }
            }

            if (!response.ok) {
                throw new Error(result?.error || `Server error: ${response.status}`);
            }

            console.log('Demo request submitted:', result);

            // Navigate to Calendly page even if backend fails
            navigate('/demo/schedule', { state: { demoData: completeData } });
        } catch (err) {
            console.error('Error submitting demo:', err);

            // Check if it's a network error
            if (err instanceof TypeError && err.message.includes('fetch')) {
                setError(t('demo.cannotConnect'));
            } else {
                const errorMessage = err instanceof Error ? err.message : 'Failed to submit. Please try again.';
                setError(errorMessage);
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-white">
            {/* Header */}
            <header className="flex items-center justify-between px-8 py-4 border-b border-gray-200">
                <div className="flex items-center gap-2">
                    <img
                        src={logo}
                        alt="Connct Logo"
                        className="h-8 object-contain"
                        onClick={() => navigate("/dashboard")}
                    />
                </div>

            </header>

            {/* Main Content */}
            <div className="flex flex-col md:flex-row min-h-[calc(100vh-73px)]">
                {/* Left Side - Light Blue Background with Image (visible on mobile and desktop) */}
                {/* Left Side - Light Blue Background with Image (visible on mobile and desktop) */}
                <div className="w-full md:w-1/2 flex items-center justify-center bg-[#E0F7FA] p-8 md:p-12 relative overflow-hidden">
                    <img src={dm2} alt="" className="max-w-[400px] w-full object-contain" />
                </div>

                {/* Right Side - Form */}
                <div className="flex-1 flex items-center justify-center p-8 lg:p-12">
                    <div className="w-full max-w-md">
                        <h1 className="text-3xl font-bold text-gray-900 mb-8">
                            {t('demo.title2')}
                        </h1>

                        {/* Form Fields */}
                        <div className="space-y-6">
                            {/* Which side of the funnel this lead is on — the admin Leads
                                screen has no other way to know before they sign up. */}
                            <SetupTrackPicker
                              value={formData.setupTrack}
                              onChange={v => handleInputChange('setupTrack', v)}
                            />

                            {/* How Can we Help You? */}
                            <div>
                                <label className="block text-sm font-medium text-gray-900 mb-2">
                                    {t('demo.helpWith')}
                                </label>
                                <select
                                    value={formData.helpWith}
                                    onChange={(e) => handleInputChange('helpWith', e.target.value)}
                                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg text-sm bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none cursor-pointer"
                                    style={{
                                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236B7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E")`,
                                        backgroundPosition: 'right 0.5rem center',
                                        backgroundRepeat: 'no-repeat',
                                        backgroundSize: '1.5em 1.5em',
                                        paddingRight: '2.5rem'
                                    }}
                                >
                                    <option value="">Select an option...</option>
                                    <option value="accounting">Accounting & Bookkeeping</option>
                                    <option value="audit">Adudit and Assurance</option>
                                    <option value="tax">Tax registration</option>

                                    <option value="other">Other</option>
                                </select>
                            </div>

                            {/* Company Name */}
                            <div>
                                <label className="block text-sm font-medium text-gray-900 mb-2">
                                    {t('demo.companyName')}
                                </label>
                                <input
                                    type="text"
                                    placeholder={t('demo.companyName')}
                                    value={formData.companyName}
                                    onChange={(e) => handleInputChange('companyName', e.target.value)}
                                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg text-sm bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>

                            {/* Tell Us What You'd Like to Discuss */}
                            <div>
                                <label className="block text-sm font-medium text-gray-900 mb-2">
                                    {t('demo.discussionTopic')}
                                </label>
                                <textarea
                                    placeholder={t('demo.discussionTopic')}
                                    value={formData.discussionTopic}
                                    onChange={(e) => handleInputChange('discussionTopic', e.target.value)}
                                    rows={4}
                                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg text-sm bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                                />
                            </div>
                        </div>

                        {/* Error Message */}
                        {error && (
                            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                                {error}
                            </div>
                        )}

                        {/* Action Buttons */}
                        <div className="flex gap-3 mt-8">
                            <button
                                onClick={handleBack}
                                disabled={isSubmitting}
                                className="px-8 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {t('demo.back')}
                            </button>
                            <button
                                onClick={handleDone}
                                disabled={isSubmitting}
                                className="flex-1 px-8 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                {isSubmitting ? (
                                    <>
                                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        <span>{t('demo.submitting')}</span>
                                    </>
                                ) : (
                                    t('demo.submit')
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
